// Változástörténet: a revízió-napló, két változat összehasonlítása és visszaállítás.
//
// A „jelenlegi változat" külön elem a listában, mert az összehasonlítás leggyakoribb esete nem két
// régi változat, hanem „mi változott azóta, hogy X szerkesztette".
//
// A lista ÁLLAPOTOKAT sorol fel (a diff a tartalmat hasonlítja, a visszaállítás a tartalmat írja
// vissza), a tárolt revízió-sor viszont a pillanatkép MELLETT a mentés metaadatait hordozza —
// vagyis a „ki, mikor, miért" egy sorral lejjebb ült, mint ahová tartozik. A hozzárendelést a
// `resolveRevisionOrigins` teszi helyre; a MIÉRT ott, a modul fejlécében van leírva.
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import type { RevisionSummary } from '@morgens-hub/contracts';
import { PERMISSIONS } from '@morgens-hub/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { AppShell } from '@/components/shared/app-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  editorKeys,
  useEditorArticle,
  useRestoreRevision,
  useRevision,
  useRevisions,
} from '@/features/editor/api';
import { ArticlePathCrumbs } from '@/features/editor/components/article-path-crumbs';
import { DiffView } from '@/features/editor/components/diff-view';
import { resolveRevisionOrigins } from '@/features/editor/lib/revision-origin';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useErrorText } from '@/lib/use-error-text';
import { useHasPermission } from '@/lib/use-permission';

// A kijelölés az URL-ben él (`?from=&to=`), nem lokális state-ben: egy „mi változott a 4. és az
// 1. változat között" összehasonlítás pont olyan megosztható nézet, mint egy keresés vagy egy
// nyitott fa-ág (lásd kereses.tsx / tudastar.tsx) — a linket be lehet küldeni egy review-hoz anélkül,
// hogy a másik félnek újra ki kéne kattintania a két revíziót.
const compareSearchSchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
});

export const Route = createFileRoute('/valtozatok/$articleId')({
  validateSearch: compareSearchSchema,
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: RevisionsPage,
});

/** A lista elemei: a jelenlegi állapot (`null`) vagy egy revízió azonosítója. */
type Selection = string | null;

function RevisionsPage(): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { articleId } = Route.useParams();

  const session = api.session.useQuery(['session']);
  const articleQuery = useEditorArticle(articleId);
  const revisionsQuery = useRevisions(articleId);
  const canRestore = useHasPermission(PERMISSIONS.revisionRestore);
  const restore = useRestoreRevision();

  // Mit hasonlítunk mihez: „innen" (from) és „eddig" (to) — mindkettő alapból a jelenlegi
  // változat (`undefined` az URL-ben → `null` itt), tehát megnyitáskor egyetlen extra kattintás
  // nélkül is van értelmes (üres, de igaz) összehasonlítás. Az „innen" rádió egy revízióra
  // állítása önmagában már „az a revízió a jelenlegihez képest" diffet ad — ez a mai alapviselkedés,
  // csak most explicit rádióval, nem a sor-kattintással.
  const { from, to } = Route.useSearch();
  const left: Selection = from ?? null;
  const right: Selection = to ?? null;

  const setLeft = (value: Selection): void => {
    void navigate({
      to: '/valtozatok/$articleId',
      params: { articleId },
      search: (prev) => ({ ...prev, from: value ?? undefined }),
      replace: true,
    });
  };
  const setRight = (value: Selection): void => {
    void navigate({
      to: '/valtozatok/$articleId',
      params: { articleId },
      search: (prev) => ({ ...prev, to: value ?? undefined }),
      replace: true,
    });
  };

  const leftQuery = useRevision(articleId, left);
  const rightQuery = useRevision(articleId, right);

  const sessionBody = session.data?.status === 200 ? session.data.body : null;
  const article = articleQuery.data?.status === 200 ? articleQuery.data.body : null;
  const revisions = revisionsQuery.data?.status === 200 ? revisionsQuery.data.body.revisions : null;

  const textOf = (selection: Selection, query: ReturnType<typeof useRevision>): string | null => {
    if (selection === null) return article?.body ?? null;
    return query.data?.status === 200 ? query.data.body.body : null;
  };
  const leftText = textOf(left, leftQuery);
  const rightText = textOf(right, rightQuery);

  // Melyik sort MELYIK változás hozta létre. A `revisions` referenciája a query-cache-ből stabil,
  // tehát a memo valóban csak új listánál számol újra.
  const origins = useMemo(() => resolveRevisionOrigins(revisions ?? []), [revisions]);

  /**
   * A sor „ki, mikor, miért" blokkja. Ha nem tudjuk, melyik változás hozta létre az állapotot
   * (a legrégebbi sor, vagy egy importált sor fölötti első saját mentés), akkor ezt KIÍRJUK —
   * a néma üres hely azt sugallná, hogy nincs is szerzője.
   */
  const originBlock = (origin: RevisionSummary | null, fallback: string): JSX.Element => (
    <>
      <span className="text-muted-foreground mt-0.5 block text-xs">
        {origin
          ? `${new Date(origin.createdAt).toLocaleString('hu-HU')} · ${
              origin.authorName || t('revisions.unknownAuthor')
            } · ${t(`revisions.source.${origin.source}`)}`
          : fallback}
      </span>
      {origin?.changeNote && <span className="mt-1 block text-xs">{origin.changeNote}</span>}
    </>
  );

  const labelOf = (selection: Selection): string => {
    if (selection === null) return t('revisions.current');
    const found = revisions?.find((r) => r.id === selection);
    return found ? t('revisions.item', { revision: found.revision }) : '—';
  };

  /**
   * Az „innen"/„eddig" rádiópár egy sorhoz. Mindkét rádió `aria-label`-je a saját revízióját
   * nevezi meg (nem csak az oszlopot) — egy két-oszlopos rádiórács oszlopfejléc nélkül is
   * értelmezhetetlen képernyőolvasóval, csak egy generikus "Innen, rádiógomb" névvel pláne.
   */
  const radioPair = (id: Selection, label: string): JSX.Element => (
    // Két `w-9` cella + `gap-1.5`, UGYANAZ a szerkezet, mint az oszlopfejléceké lentebb — a
    // rádiók így a fejléccel anélkül igazodnak, hogy a konténernek saját (hardcode-olt) szélessége
    // lenne.
    <div className="mt-0.5 flex shrink-0 gap-1.5">
      <div className="flex w-9 justify-center">
        <input
          type="radio"
          name="revisions-from"
          className="h-4 w-4"
          aria-label={t('revisions.compareFrom', { revision: label })}
          checked={left === id}
          onChange={() => {
            setLeft(id);
          }}
        />
      </div>
      <div className="flex w-9 justify-center">
        <input
          type="radio"
          name="revisions-to"
          className="h-4 w-4"
          aria-label={t('revisions.compareTo', { revision: label })}
          checked={right === id}
          onChange={() => {
            setRight(id);
          }}
        />
      </div>
    </div>
  );

  const onRestore = (revisionId: string, revisionNumber: number): void => {
    if (!article) return;
    if (!window.confirm(t('revisions.restoreConfirm', { revision: revisionNumber }))) return;

    restore.mutate(
      { params: { id: articleId, revisionId }, body: { version: article.version } },
      {
        onSuccess: (response) => {
          queryClient.setQueryData(editorKeys.article(articleId), {
            status: 200,
            body: response.body,
          });
          void queryClient.invalidateQueries({ queryKey: ['knowledge'] });
          toast.success(t('revisionRestored', { ns: 'toast', revision: revisionNumber }));
          void navigate({ to: '/szerkesztes/$articleId', params: { articleId } });
        },
        onError: (failure: unknown) => {
          toast.error(errorText(failure, t('revisions.restoreOperation')));
        },
      },
    );
  };

  return (
    <AppShell session={sessionBody}>
      <div className="space-y-6">
        {/* A morzsa itt is teljes: a változástörténetből eddig CSAK a szerkesztőbe lehetett
            visszalépni, a cikkhez és a hierarchiához nem. */}
        {article && <ArticlePathCrumbs article={article} current={t('revisions.title')} />}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('revisions.title')}</h1>
            {article && <p className="text-muted-foreground mt-1 text-sm">{article.title}</p>}
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/szerkesztes/$articleId" params={{ articleId }}>
              <ArrowUturnLeftIcon className="mr-1.5 h-4 w-4" aria-hidden />
              {t('revisions.backToEditor')}
            </Link>
          </Button>
        </div>

        {revisionsQuery.isLoading && <Skeleton className="h-40 w-full" />}

        {revisionsQuery.isError && (
          <p className="text-destructive text-sm">{t('revisions.loadError')}</p>
        )}

        {revisions?.length === 0 && (
          <p className="text-muted-foreground text-sm">{t('revisions.empty')}</p>
        )}

        {revisions && revisions.length > 0 && (
          <div className="grid gap-8 lg:grid-cols-[22rem_1fr]">
            <div>
              <p className="text-muted-foreground mb-2 text-xs">{t('revisions.compareHint')}</p>
              {/* Oszlopfejlécek a rádiópárhoz — csak látvány, a valódi akadálymentes névadás
                  soronként, az egyes rádiókon van (lásd radioPair). */}
              <div className="flex gap-1.5 pl-1 text-xs font-medium" aria-hidden>
                <span className="w-9 text-center">{t('revisions.compareFromColumn')}</span>
                <span className="w-9 text-center">{t('revisions.compareToColumn')}</span>
              </div>

              <ol className="mt-1 space-y-2" aria-label={t('revisions.title')}>
                <li>
                  <div
                    className={`border-border flex items-start gap-3 rounded-lg border p-3 transition-colors duration-150 ${
                      left === null || right === null ? 'bg-muted' : ''
                    }`}
                  >
                    {radioPair(null, t('revisions.current'))}
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{t('revisions.current')}</span>
                      {/* A most élő tartalmat a LEGUTÓBBI mentés hozta létre — annak a mentésnek az
                          adatai a legfrissebb revízión ülnek. Ez volt a bejelentett hiba: a
                          frissen írt megjegyzés eddig egy sorral lejjebb jelent meg. */}
                      {originBlock(origins.current, t('revisions.currentHint'))}
                    </div>
                  </div>
                </li>

                {revisions.map((revision) => {
                  const label = t('revisions.item', { revision: revision.revision });
                  return (
                    <li key={revision.id}>
                      <div
                        className={`border-border flex items-start gap-3 rounded-lg border p-3 transition-colors duration-150 ${
                          left === revision.id || right === revision.id ? 'bg-muted' : ''
                        }`}
                      >
                        {radioPair(revision.id, label)}
                        <div className="min-w-0 flex-1">
                          <span className="font-medium">{label}</span>
                          {originBlock(
                            origins.byRevisionId.get(revision.id) ?? null,
                            t('revisions.originUnknown'),
                          )}

                          {canRestore && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="mt-2"
                              disabled={restore.isPending || !article}
                              onClick={() => {
                                onRestore(revision.id, revision.revision);
                              }}
                            >
                              {restore.isPending
                                ? t('revisions.restoring')
                                : t('revisions.restore')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            <section>
              <h2 className="text-lg font-medium">
                {t('revisions.diffTitle', { from: labelOf(left), to: labelOf(right) })}
              </h2>
              <div className="mt-3">
                {leftText !== null && rightText !== null ? (
                  <DiffView oldText={leftText} newText={rightText} />
                ) : (
                  <Skeleton className="h-40 w-full" />
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </AppShell>
  );
}
